"use client"

import { Instagram, Linkedin, ExternalLink } from 'lucide-react';
import React, { useEffect, useState, useRef } from "react"
import { InstagramPostCard } from "./InstagramPostCard"
import { LinkedInPostCard } from "./LinkedInPostCard"
import AOS from "aos"
import "aos/dist/aos.css"
import StarTrail from "../ui/StarTrail"

export default function SocialMediaWall() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const wallRef = useRef(null)

  useEffect(() => {
    AOS.init({ duration: 1000 })
    // Scroll to top
    window.scrollTo(0, 0)

    const fetchPosts = async () => {
      try {
        const res = await fetch("/api/posts")
        const data = await res.json()
        const sortedPosts = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        setPosts(sortedPosts)
      } catch (error) {
        console.error("❌ Error al cargar los posts sociales:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [])

  // Filtrar posts por origen
  const instagramPosts = posts.filter(p => p.source === "INSTAGRAM")
  const linkedInPosts = posts.filter(p => p.source === "LINKEDIN" || !p.source)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <main className="min-h-screen bg-white py-24 px-4 relative overflow-hidden" ref={wallRef}>
      <StarTrail containerRef={wallRef} />
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16" data-aos="fade-down">
          <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tight" 
              style={{
                fontSize: "3.2rem",
                fontWeight: 900,
                background: "linear-gradient(135deg, #6366f1 0%, #a855f1 50%, #ec4899 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
                letterSpacing: "-0.02em",
                marginBottom: "1.5rem",
                marginTop: "-50px"
              }}>
            Nuestras Redes
          </h1>
          <p className="text-muted-foreground text-lg md:text-2xl max-w-3xl mx-auto font-medium leading-relaxed" style={{
            fontSize: "1.25rem",
            lineHeight: "1.6",
            color: "#4b5563",
            maxWidth: "600px",
            margin: "0 auto"
          }}>
            Mantente al día con nuestras últimas publicaciones y novedades
          </p>
        </div>

        {/* Social Media Posts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 justify-items-center">
          
          {/* Instagram Section */}
          <div className="flex flex-col items-center gap-6 w-full" data-aos="fade-right">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Instagram
            </h2>
            
            {instagramPosts.length > 0 ? (
              instagramPosts.map(post => (
                <InstagramPostCard
                  key={post.id}
                  username="sparklingtech.es"
                  userAvatar="/img/logo1.png"
                  location="Las Palmas, Spain"
                  postImage={post.imagenUrl}
                  likes={Math.floor(Math.random() * 500) + 100} // Mock de likes
                  caption={post.contenido}
                  comments={Math.floor(Math.random() * 20)}
                  timeAgo={post.fecha}
                  isVerified={true}
                />
              ))
            ) : (
              <p className="text-muted-foreground italic">No hay publicaciones recientes de Instagram.</p>
            )}

            <a
              href="https://www.instagram.com/sparklingtech.es/"
              target="_blank"
              rel="noopener noreferrer"
              className="lit-up-button"
            >
              <div className="lit-up-border" />
              <div className="lit-up-content">
                Ver más en Instagram
              </div>
            </a>
          </div>

          {/* LinkedIn Section */}
          <div className="flex flex-col items-center gap-6 w-full" data-aos="fade-left">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <svg className="w-6 h-6 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </h2>
            
            {linkedInPosts.length > 0 ? (
              linkedInPosts.map(post => (
                <LinkedInPostCard
                  key={post.id}
                  name="Sparkling Tech Solutions"
                  avatar="/img/logo1.png"
                  headline="Innovación Digital | StartUp ULPGC"
                  timeAgo={post.fecha}
                  content={post.contenido}
                  postImage={post.imagenUrl}
                  reactions={Math.floor(Math.random() * 200) + 50}
                  comments={Math.floor(Math.random() * 15)}
                  reposts={Math.floor(Math.random() * 10)}
                />
              ))
            ) : (
              <p className="text-muted-foreground italic">No hay publicaciones recientes de LinkedIn.</p>
            )}

            <div className="social-cta" data-aos="fade-up" data-aos-delay="200">
            <a
              href="https://es.linkedin.com/company/sparklingtechsolutions"
              target="_blank"
              rel="noopener noreferrer"
              className="lit-up-button"
            >
              <div className="lit-up-border" />
              <div className="lit-up-content">
                Ver más en LinkedIn
              </div>
            </a>
          </div>

          </div>

        </div>
      </div>
    </main>
  );
}
